/**
 * API Service
 *
 * Centralized HTTP client for backend communication.
 * Uses axios with automatic error handling.
 */

import axios from 'axios';


// -----------------------------------------------------------------------------
// Configuration
// -----------------------------------------------------------------------------

const API_BASE = '/api';

const client = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});


// -----------------------------------------------------------------------------
// Error Handler
// -----------------------------------------------------------------------------

/**
 * Extract error message from axios error and throw with meaningful message.
 *
 * @param {Error} error - The axios error object.
 * @param {string} defaultMessage - Fallback message if none found.
 * @throws {Error} - Always throws with extracted or default message.
 */
function handleError(error, defaultMessage) {
  const message = error.response?.data?.error || error.message || defaultMessage;
  console.error(`[API Error] ${message}`);
  throw new Error(message);
}


// -----------------------------------------------------------------------------
// Game API
// -----------------------------------------------------------------------------

export const gameAPI = {
  /**
   * Start a new game session.
   *
   * @returns {Promise<Object>} - Session data with initialWord and targetWord.
   */
  async startGame(difficulty = 50) {
    try {
      const response = await client.post('/game/start', { difficulty });
      return response.data;
    } catch (error) {
      handleError(error, 'Failed to start game');
    }
  },

  /**
   * Expand a word to find similar words.
   *
   * @param {string} sessionId - Game session identifier.
   * @param {string} word - Word to expand.
   * @returns {Promise<Object>} - Expansion results with similar words and game state.
   */
  async expandWord(sessionId, word) {
    try {
      const response = await client.post('/game/expand', { sessionId, word });
      return response.data;
    } catch (error) {
      handleError(error, 'Failed to expand word');
    }
  },

  /**
   * Combine two words to find their semantic midpoint.
   *
   * @param {string} sessionId - Game session identifier.
   * @param {string} word1 - First word.
   * @param {string} word2 - Second word.
   * @param {string[]} existingWords - Words already on the graph (to exclude).
   * @returns {Promise<Object>} - Combined word result and game state.
   */
  async combineWords(sessionId, word1, word2, existingWords = []) {
    try {
      const response = await client.post('/game/combine', {
        sessionId, word1, word2, existingWords,
      });
      return response.data;
    } catch (error) {
      handleError(error, 'Failed to combine words');
    }
  },
};